import React, { useEffect, useRef, useState } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Card from '@amzn/meridian/card';
import Tag from '@amzn/meridian/tag';
import Box from '@amzn/meridian/box';
import Input from '@amzn/meridian/input';
import SearchField from '@amzn/meridian/search-field';
import Alert from '@amzn/meridian/alert';
import Sheet from '@amzn/meridian/sheet';
import Icon from '@amzn/meridian/icon';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import LinearGauge from '@amzn/meridian/linear-gauge';
import closeTokens from '@amzn/meridian-tokens/base/icon/close-small';
import { getAsnMetricsService, getUploadService } from '../services';
import type { AsnFailure, AsnMetrics, IntegrationCounts } from '../services/types';

// Supplier Gateway · ASN (Advance Shipping Notice). Summary/worklist render
// empty/zero states until the backend is wired; the upload uses the real
// upload service.

type Preset = '24h' | '7d' | '30d';
type Integration = 'API' | 'File Drop' | 'Presigned URL';

// Selected date range: one of the presets, or a custom From/To window.
type ActiveRange =
    | { kind: 'preset'; preset: Preset }
    | { kind: 'custom'; from: string; to: string; days: number };

const INTEGRATIONS: Integration[] = ['API', 'File Drop', 'Presigned URL'];
const PRESETS: Preset[] = ['24h', '7d', '30d'];

const TAG_BY_INTEGRATION: Record<Integration, string> = {
    API: 'REST',
    'File Drop': 'CSV or Excel',
    'Presigned URL': 'S3',
};

// UI label -> backend integration/source key returned by the metrics API.
const SOURCE_BY_INTEGRATION: Record<Integration, string> = {
    API: 'API',
    'File Drop': 'FILE_DROP',
    'Presigned URL': 'PRESIGNED_URL',
};

const EMPTY_COUNTS: IntegrationCounts = { total: 0, dataErrors: 0, businessErrors: 0 };

// Resolve the active range to an ISO [from, to] window the metrics API expects.
function rangeToWindow(r: ActiveRange): { from: string; to: string } {
    const now = new Date();
    const to = now.toISOString();
    if (r.kind === 'custom') {
        // Inclusive day window: start of `from` to end of `to`.
        return {
            from: new Date(`${r.from}T00:00:00Z`).toISOString(),
            to: new Date(`${r.to}T23:59:59Z`).toISOString(),
        };
    }
    const daysByPreset: Record<Preset, number> = { '24h': 1, '7d': 7, '30d': 30 };
    const from = new Date(now.getTime() - daysByPreset[r.preset] * 86400000).toISOString();
    return { from, to };
}

const rangeLabel = (r: ActiveRange): string =>
    r.kind === 'preset' ? r.preset : `${r.from} → ${r.to}`;

// Inclusive day count between two yyyy-mm-dd dates; 0 if invalid/reversed.
function daysBetween(from: string, to: string): number {
    if (!from || !to) return 0;
    const a = new Date(from).getTime();
    const b = new Date(to).getTime();
    if (Number.isNaN(a) || Number.isNaN(b) || b < a) return 0;
    return Math.round((b - a) / 86400000) + 1;
}

const PENDING_HEADERS = [
    'Line #',
    'PO',
    'Item',
    'Description',
    'Ordered',
    'Received',
    'Outstanding',
    'Need-by date',
    'Status',
];

// "Coming up" horizon options for the Pending ASNs worklist.
const PENDING_HORIZONS = [7, 14, 30, 'all'] as const;
type Horizon = (typeof PENDING_HORIZONS)[number];
const horizonLabel = (h: Horizon) => (h === 'all' ? '30+' : `${h}d`);

// CSV export columns for the pending worklist.
const PENDING_EXPORT_HEADERS = [
    'Line #',
    'PO',
    'Item',
    'Description',
    'Ordered',
    'Received',
    'Outstanding',
    'Need by date',
    'Bucket',
];

function downloadCsv(filename: string, content: string) {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

type Bucket = 'data' | 'business';

// ── Per-integration summary card (counts empty until backend is wired) ────────
function FailCount({
    label,
    count,
    color,
    onClick,
}: {
    label: string;
    count: number;
    color: 'warning' | 'error';
    onClick: () => void;
}) {
    return (
        <Button type="link" disabled={count === 0} onClick={onClick}>
            <Column spacing="100" alignmentHorizontal="start">
                <Text type="b100" color="secondary">
                    {label}
                </Text>
                <Row spacing="100" alignmentVertical="baseline">
                    <Text type="h300" color={count ? color : 'secondary'}>
                        {String(count)}
                    </Text>
                    {count > 0 ? <Text type="b100">view →</Text> : null}
                </Row>
            </Column>
        </Button>
    );
}

function IntegrationCard({
    intg,
    counts,
    onViewFailures,
}: {
    intg: Integration;
    counts: IntegrationCounts;
    onViewFailures: (bucket: Bucket) => void;
}) {
    const { total, dataErrors, businessErrors } = counts;
    return (
        <Card spacingInset="none">
            <Column spacing="none">
                <Box spacingInset="400" type="fill">
                    <Row alignmentHorizontal="justify" alignmentVertical="center">
                        <Text type="h300">{intg}</Text>
                        <Tag type="theme">{TAG_BY_INTEGRATION[intg]}</Tag>
                    </Row>
                </Box>
                <Box spacingInset="400">
                    <Column spacing="300">
                        <Column spacing="100">
                            <Text type="b100" color="secondary">
                                Total ASNs
                            </Text>
                            <Text type="d100">{String(total)}</Text>
                        </Column>
                        <Row spacing="400" widths="fill">
                            <FailCount
                                label="Data errors"
                                count={dataErrors}
                                color="warning"
                                onClick={() => onViewFailures('data')}
                            />
                            <FailCount
                                label="Business errors"
                                count={businessErrors}
                                color="error"
                                onClick={() => onViewFailures('business')}
                            />
                        </Row>
                    </Column>
                </Box>
            </Column>
        </Card>
    );
}

// ── Pending ASNs (empty until backend is wired) ───────────────────────────────

// One titled section (Overdue / Coming up) with its own header + empty table.
function PendingSection({
    title,
    tone,
    note,
    count,
    emptyText,
}: {
    title: string;
    tone: 'red' | 'neutral';
    note: string;
    count: number;
    emptyText: string;
}) {
    return (
        <Column spacing="none">
            <Box spacingInset="300 400" type="fill" backgroundColor={tone === 'red' ? undefined : 'subdued'}>
                <Row alignmentVertical="center" spacing="200">
                    <Text type="b300" color={tone === 'red' ? 'error' : undefined}>
                        {title}
                    </Text>
                    <Text type="b100" color="secondary">
                        {note}
                    </Text>
                    <Box width="100%" />
                    <Text type="b300" color={tone === 'red' ? 'error' : undefined}>
                        {String(count)}
                    </Text>
                </Row>
            </Box>
            <Table headerRows={1} showDividers spacing="small">
                <TableRow>
                    {PENDING_HEADERS.map((h) => (
                        <TableCell key={h}>{h}</TableCell>
                    ))}
                </TableRow>
                <TableRow>
                    <TableCell columnSpan={PENDING_HEADERS.length} alignmentHorizontal="center">
                        <Box spacingInset="500">
                            <Text color="secondary">{emptyText}</Text>
                        </Box>
                    </TableCell>
                </TableRow>
            </Table>
        </Column>
    );
}

function PendingAsns() {
    const [horizon, setHorizon] = useState<Horizon>(14);

    // No backend yet, so both sections are empty. Export still produces a valid
    // (header-only) CSV so the flow works end-to-end.
    const exportCsv = () => downloadCsv('pending-asns.csv', `${PENDING_EXPORT_HEADERS.join(',')}\n`);

    return (
        <Card spacingInset="none">
            <Column spacing="none">
                <Box spacingInset="400" type="fill">
                    <Row alignmentHorizontal="justify" alignmentVertical="center" wrap="down" spacing="300">
                        <Column spacing="100">
                            <Text type="h300">Pending ASNs</Text>
                            <Text type="b100" color="secondary">
                                What still needs an ASN: everything overdue, plus what&rsquo;s coming
                                up. Received = units you&rsquo;ve already notified.
                            </Text>
                        </Column>
                        <Row alignmentVertical="center" spacing="200">
                            <Text type="b100" color="secondary">
                                Coming up:
                            </Text>
                            <Row spacing="100">
                                {PENDING_HORIZONS.map((h) => (
                                    <Button
                                        key={String(h)}
                                        type={horizon === h ? 'primary' : 'tertiary'}
                                        size="small"
                                        onClick={() => setHorizon(h)}
                                    >
                                        {horizonLabel(h)}
                                    </Button>
                                ))}
                            </Row>
                            <Button type="secondary" size="small" onClick={exportCsv}>
                                ⤓ Export
                            </Button>
                        </Row>
                    </Row>
                </Box>

                <PendingSection
                    title={horizon === 'all' ? 'Overdue 30+ days' : 'Overdue'}
                    tone="red"
                    note={
                        horizon === 'all'
                            ? 'overdue by more than 30 days — escalate'
                            : 'past need-by — ship and notify now'
                    }
                    count={0}
                    emptyText={
                        horizon === 'all'
                            ? 'Nothing overdue by more than 30 days.'
                            : 'Nothing overdue.'
                    }
                />
                <PendingSection
                    title={horizon === 'all' ? 'Due beyond 30 days' : 'Coming up'}
                    tone="neutral"
                    note={
                        horizon === 'all'
                            ? 'need-by more than 30 days out'
                            : `due within the next ${horizon} days`
                    }
                    count={0}
                    emptyText={
                        horizon === 'all'
                            ? 'Nothing due beyond 30 days.'
                            : `Nothing due in the next ${horizon} days.`
                    }
                />
            </Column>
        </Card>
    );
}

// ── Upload ASN file (wired to the real upload service) ────────────────────────
function UploadAsn() {
    const inputRef = useRef<HTMLInputElement>(null);
    const [fileName, setFileName] = useState<string | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [progress, setProgress] = useState<number | null>(null);
    const [status, setStatus] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [submitted, setSubmitted] = useState(false);

    const onPick = (f: File | undefined) => {
        if (f) {
            setFile(f);
            setFileName(f.name);
            setSubmitted(false);
            setStatus(null);
            setError(null);
        }
    };

    const reset = () => {
        setFile(null);
        setFileName(null);
        setSubmitted(false);
        setStatus(null);
        setError(null);
        if (inputRef.current) inputRef.current.value = '';
    };

    const submit = async () => {
        if (!file) return;
        setError(null);
        setStatus(null);
        setProgress(0);
        try {
            await getUploadService().uploadFile(file, setProgress);
            setSubmitted(true);
            setStatus(`Uploaded ${file.name} successfully.`);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Upload failed.');
        } finally {
            setProgress(null);
        }
    };

    if (submitted) {
        return (
            <Card>
                <Column alignmentHorizontal="center" spacing="300" spacingInset="600">
                    <Tag type="success">OK</Tag>
                    <Text type="h300">ASN submitted</Text>
                    <Text color="secondary">{status}</Text>
                    <Button type="secondary" onClick={reset}>
                        Upload another file
                    </Button>
                </Column>
            </Card>
        );
    }

    return (
        <Column spacing="400">
            <div
                role="button"
                tabIndex={0}
                onClick={() => inputRef.current?.click()}
                onKeyDown={(ev) => {
                    if (ev.key === 'Enter' || ev.key === ' ') {
                        ev.preventDefault();
                        inputRef.current?.click();
                    }
                }}
                onDragOver={(ev) => ev.preventDefault()}
                onDrop={(ev) => {
                    ev.preventDefault();
                    onPick(ev.dataTransfer.files?.[0]);
                }}
                style={{ cursor: 'pointer' }}
            >
                <Box type="outline" spacingInset="600" backgroundColor="subdued">
                    <Column alignmentHorizontal="center" spacing="100">
                        <Text type="h400">⬆</Text>
                        <Text type="h300">
                            {fileName ? 'Choose a different file' : 'Drop your ASN file here or browse'}
                        </Text>
                        <Text color="secondary">CSV or Excel (.csv, .xlsx) · up to 25 MB</Text>
                    </Column>
                </Box>
                <input
                    id="asn-file-input"
                    ref={inputRef}
                    type="file"
                    accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    style={{ display: 'none' }}
                    onChange={(ev) => onPick(ev.target.files?.[0])}
                />
            </div>

            {fileName ? (
                <Card spacingInset="none">
                    <Column spacing="none">
                        <Box spacingInset="300 400" type="fill">
                            <Row alignmentHorizontal="justify" alignmentVertical="center">
                                <Text type="b300">{fileName}</Text>
                                <Tag type="theme">Ready to submit</Tag>
                            </Row>
                        </Box>
                        {progress !== null ? (
                            <Box spacingInset="300 400">
                                <LinearGauge
                                    type="fill"
                                    minValue={0}
                                    maxValue={100}
                                    value={progress}
                                    aria-label={`Upload progress: ${progress}%`}
                                />
                            </Box>
                        ) : null}
                        {error ? (
                            <Box spacingInset="300 400">
                                <Alert type="error" size="small">
                                    {error}
                                </Alert>
                            </Box>
                        ) : null}
                        <Box spacingInset="400">
                            <Row alignmentHorizontal="justify" alignmentVertical="center">
                                <Button type="tertiary" onClick={reset}>
                                    Remove file
                                </Button>
                                <Button type="primary" onClick={submit} disabled={progress !== null}>
                                    Submit ASN
                                </Button>
                            </Row>
                        </Box>
                    </Column>
                </Card>
            ) : null}
        </Column>
    );
}

// ── Failed transactions drawer ────────────────────────────────────────────────
const FAILURE_HEADERS = ['Submission', 'ASN', 'Integration', 'Received', 'Error'];

// Reverse of SOURCE_BY_INTEGRATION: backend source key -> UI integration label.
const INTEGRATION_BY_SOURCE: Record<string, Integration> = {
    API: 'API',
    FILE_DROP: 'File Drop',
    PRESIGNED_URL: 'Presigned URL',
};

const FAILURE_PAGE_SIZE = 50;

// One fetched row plus its resolved bucket, so the panel can filter/label it.
interface LoadedFailure extends AsnFailure {
    bucket: Bucket;
}

/**
 * Slide-over panel listing failed submissions for the selected range. Fetches
 * from the failures API for the selected source + bucket (iterating the
 * relevant combinations when "all" is chosen), honors the dashboard's login
 * filter, and pages via the backend cursor ("Load more").
 */
function FailuresPanel({
    range,
    login,
    initIntegration,
    initBucket,
    onClose,
}: {
    range: ActiveRange;
    login?: string;
    initIntegration: Integration | 'all';
    initBucket: Bucket | 'all';
    onClose: () => void;
}) {
    const [q, setQ] = useState('');
    const [integration, setIntegration] = useState<Integration | 'all'>(initIntegration);
    const [bucket, setBucket] = useState<Bucket | 'all'>(initBucket);

    const [rows, setRows] = useState<LoadedFailure[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Per (source, bucket) next-page cursor; presence of any means "more".
    const [cursors, setCursors] = useState<Record<string, string>>({});

    // Which (source, bucket) combinations the current filters select.
    const activeSources = (): string[] =>
        integration === 'all'
            ? INTEGRATIONS.map((i) => SOURCE_BY_INTEGRATION[i])
            : [SOURCE_BY_INTEGRATION[integration]];
    const activeBuckets = (): Bucket[] => (bucket === 'all' ? ['data', 'business'] : [bucket]);

    // Fetch a page for each active (source, bucket). `reset` starts fresh (new
    // filter/range); otherwise appends the next page using stored cursors. The
    // per-combo fetches run in parallel (no sequential loop).
    const load = React.useCallback(
        async (reset: boolean) => {
            const { from, to } = rangeToWindow(range);
            const svc = getAsnMetricsService();

            // Every (source, bucket) combo the filters select.
            const combos = activeSources().flatMap((source) =>
                activeBuckets().map((b) => ({ source, b, key: `${source}#${b}` })),
            );
            // On append, only combos that still have a cursor are refetched.
            const toFetch = reset ? combos : combos.filter((c) => cursors[c.key]);
            if (toFetch.length === 0) return;

            setLoading(true);
            setError(null);
            try {
                const pages = await Promise.all(
                    toFetch.map((c) =>
                        svc
                            .getFailures({
                                source: c.source,
                                bucket: c.b,
                                from,
                                to,
                                identityId: login || undefined,
                                limit: FAILURE_PAGE_SIZE,
                                cursor: reset ? undefined : cursors[c.key],
                            })
                            .then((page) => ({ combo: c, page })),
                    ),
                );

                const collected = pages.flatMap(({ combo, page }) =>
                    page.failures.map((f) => ({ ...f, bucket: combo.b })),
                );
                const nextCursors = pages.reduce<Record<string, string>>((acc, { combo, page }) => {
                    if (page.nextCursor) acc[combo.key] = page.nextCursor;
                    return acc;
                }, {});

                setRows((prev) => (reset ? collected : [...prev, ...collected]));
                setCursors((prev) => (reset ? nextCursors : { ...prev, ...nextCursors }));
            } catch (e) {
                setError(e instanceof Error ? e.message : 'Failed to load failures.');
            } finally {
                setLoading(false);
            }
        },
        [range, login, integration, bucket, cursors],
    );

    // Reset + fetch whenever the range, login, or filters change. A single
    // resetKey collapses those into one dependency; `load` is intentionally not
    // a dependency (it also changes on cursor updates, which is the append path
    // and must not trigger a reset).
    const resetKey = `${rangeLabel(range)}#${login ?? ''}#${integration}#${bucket}`;
    const loadRef = React.useRef(load);
    loadRef.current = load;
    useEffect(() => {
        setRows([]);
        setCursors({});
        loadRef.current(true).catch(() => undefined);
    }, [resetKey]);

    const hasMore = Object.keys(cursors).length > 0;

    // Client-side search within the loaded page(s): match submission id, any
    // ASN number, or the error text.
    const query = q.trim().toLowerCase();
    const visible = query
        ? rows.filter(
              (r) =>
                  r.submissionId.toLowerCase().includes(query) ||
                  r.asnNumber.toLowerCase().includes(query) ||
                  r.error.toLowerCase().includes(query),
          )
        : rows;

    const chip = (label: string, active: boolean, onClick: () => void) => (
        <Button type={active ? 'primary' : 'tertiary'} size="small" onClick={onClick}>
            {label}
        </Button>
    );

    return (
        <Sheet type="overlay" side="end" open onClose={onClose}>
            <Column spacing="400" width="100%" minWidth={360} maxWidth={700}>
                <Row alignmentHorizontal="justify" alignmentVertical="center">
                    <Column spacing="100">
                        <Text type="h300">Failed transactions</Text>
                        <Text type="b100" color="secondary">
                            Range: {rangeLabel(range)} · Data errors = what you sent, Business errors
                            = order match
                        </Text>
                    </Column>
                    <Button type="icon" label="Close failed transactions" onClick={onClose}>
                        <Icon tokens={closeTokens}>Close</Icon>
                    </Button>
                </Row>

                <Row spacing="200" widths="fill">
                    {INTEGRATIONS.map((i) => {
                        const src = SOURCE_BY_INTEGRATION[i];
                        const n = visible.filter((r) => r.source === src).length;
                        return (
                            <Box key={i} type="fill" backgroundColor="subdued" spacingInset="300">
                                <Column spacing="100">
                                    <Text type="b100" color="secondary">
                                        {i}
                                    </Text>
                                    <Text type="h300">
                                        {n}
                                        {hasMore ? '+' : ''} loaded
                                    </Text>
                                </Column>
                            </Box>
                        );
                    })}
                </Row>

                <Column spacing="200">
                    <SearchField
                        value={q}
                        onChange={setQ}
                        onSubmit={() => undefined}
                        placeholder="Search by ASN, PO, or line #…"
                        label="Search failed transactions"
                    />
                    <Row spacing="100" wrap="down" alignmentVertical="center">
                        {chip('All types', bucket === 'all', () => setBucket('all'))}
                        {chip('Data errors', bucket === 'data', () => setBucket('data'))}
                        {chip('Business errors', bucket === 'business', () => setBucket('business'))}
                        {chip('All integrations', integration === 'all', () =>
                            setIntegration('all'),
                        )}
                        {INTEGRATIONS.map((i) =>
                            chip(i, integration === i, () => setIntegration(i)),
                        )}
                    </Row>
                </Column>

                <Table headerRows={1} showDividers spacing="small">
                    <TableRow>
                        {FAILURE_HEADERS.map((h) => (
                            <TableCell key={h}>{h}</TableCell>
                        ))}
                    </TableRow>
                    {visible.map((r) => (
                        <TableRow key={`${r.submissionId}#${r.asnNumber}#${r.bucket}`}>
                            <TableCell>
                                <Text type="b100" fontFamily="amazonEmberMonospace">
                                    {r.submissionId}
                                </Text>
                            </TableCell>
                            <TableCell>{r.asnNumber || '—'}</TableCell>
                            <TableCell>{INTEGRATION_BY_SOURCE[r.source] ?? r.source}</TableCell>
                            <TableCell>
                                <Text color="secondary">
                                    {new Date(r.receivedAt).toLocaleString()}
                                </Text>
                            </TableCell>
                            <TableCell>
                                <Text color={r.bucket === 'data' ? 'warning' : 'error'}>
                                    {r.error || '—'}
                                </Text>
                            </TableCell>
                        </TableRow>
                    ))}
                    {!loading && visible.length === 0 ? (
                        <TableRow>
                            <TableCell columnSpan={FAILURE_HEADERS.length} alignmentHorizontal="center">
                                <Box spacingInset="500">
                                    <Text color="secondary">
                                        {error ?? 'No failed transactions in this range.'}
                                    </Text>
                                </Box>
                            </TableCell>
                        </TableRow>
                    ) : null}
                    {loading ? (
                        <TableRow>
                            <TableCell columnSpan={FAILURE_HEADERS.length} alignmentHorizontal="center">
                                <Box spacingInset="500">
                                    <Text color="secondary">Loading…</Text>
                                </Box>
                            </TableCell>
                        </TableRow>
                    ) : null}
                </Table>
                {error && visible.length > 0 ? (
                    <Text type="b100" color="error">
                        {error}
                    </Text>
                ) : null}
                {hasMore && !loading ? (
                    <Row alignmentHorizontal="center">
                        <Button
                            type="secondary"
                            size="small"
                            onClick={() => {
                                load(false).catch(() => undefined);
                            }}
                        >
                            Load more
                        </Button>
                    </Row>
                ) : null}
            </Column>
        </Sheet>
    );
}

FailuresPanel.defaultProps = {
    login: undefined,
};

// ── Main screen ───────────────────────────────────────────────────────────────
export default function AsnScreen() {
    const [range, setRange] = useState<ActiveRange>({ kind: 'preset', preset: '7d' });
    const [showCustom, setShowCustom] = useState(false);
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [drawer, setDrawer] = useState<{
        integration: Integration | 'all';
        bucket: Bucket | 'all';
    } | null>(null);

    const isPreset = (p: Preset) => range.kind === 'preset' && range.preset === p;
    const customDays = daysBetween(from, to);
    const applyCustom = () => {
        if (customDays > 0) setRange({ kind: 'custom', from, to, days: customDays });
    };

    // Per-integration counts for the selected range, fetched from the metrics API.
    const [metrics, setMetrics] = useState<AsnMetrics | null>(null);
    const [metricsError, setMetricsError] = useState<string | null>(null);

    useEffect(() => {
        let active = true;
        const { from: f, to: t } = rangeToWindow(range);
        setMetricsError(null);
        getAsnMetricsService()
            .getMetrics(f, t)
            .then((m) => {
                if (active) setMetrics(m);
            })
            .catch((e) => {
                if (active)
                    setMetricsError(e instanceof Error ? e.message : 'Failed to load metrics.');
            });
        return () => {
            active = false;
        };
    }, [range]);

    const countsFor = (intg: Integration): IntegrationCounts =>
        metrics?.byIntegration[SOURCE_BY_INTEGRATION[intg]] ?? EMPTY_COUNTS;

    const totalFailures = INTEGRATIONS.reduce((sum, intg) => {
        const c = countsFor(intg);
        return sum + c.dataErrors + c.businessErrors;
    }, 0);

    return (
        <Column spacing="400" width="100%">
            {drawer ? (
                <FailuresPanel
                    range={range}
                    initIntegration={drawer.integration}
                    initBucket={drawer.bucket}
                    onClose={() => setDrawer(null)}
                />
            ) : null}

            {/* Header: title + failures button + range presets */}
            <Row alignmentHorizontal="justify" alignmentVertical="top" wrap="down" spacing="300">
                <Column spacing="100">
                    <Text type="h400">ASN — Advance Shipping Notice</Text>
                    <Text color="secondary">
                        See what&rsquo;s pending, upload new ASN files, and review your submission
                        summary.
                    </Text>
                </Column>
                <Row alignmentVertical="center" spacing="200">
                    <Button
                        type="secondary"
                        onClick={() => setDrawer({ integration: 'all', bucket: 'all' })}
                    >
                        <Row spacing="200" alignmentVertical="center">
                            <Text color={totalFailures ? 'error' : 'secondary'}>
                                View all failures
                            </Text>
                            <Tag type={totalFailures ? 'error' : 'neutral'}>
                                {String(totalFailures)}
                            </Tag>
                        </Row>
                    </Button>
                    <Row spacing="100">
                        {PRESETS.map((p) => (
                            <Button
                                key={p}
                                type={isPreset(p) ? 'primary' : 'tertiary'}
                                size="small"
                                onClick={() => {
                                    setRange({ kind: 'preset', preset: p });
                                    setShowCustom(false);
                                }}
                            >
                                {p}
                            </Button>
                        ))}
                        <Button
                            type={range.kind === 'custom' ? 'primary' : 'tertiary'}
                            size="small"
                            onClick={() => setShowCustom((s) => !s)}
                        >
                            Custom
                        </Button>
                    </Row>
                </Row>
            </Row>

            {/* Custom date range */}
            {showCustom ? (
                <Card>
                    <Row alignmentVertical="bottom" spacing="300" wrap="down">
                        <Input
                            type="date"
                            label="From"
                            width={160}
                            value={from}
                            onChange={setFrom}
                        />
                        <Input type="date" label="To" width={160} value={to} onChange={setTo} />
                        <Button type="primary" onClick={applyCustom}>
                            Submit
                        </Button>
                        <Text color={customDays > 0 ? 'secondary' : 'error'}>
                            {customDays > 0
                                ? `${customDays} day(s) selected`
                                : 'Select a valid range (To must be on/after From)'}
                        </Text>
                    </Row>
                </Card>
            ) : null}

            {/* Integration summary cards */}
            <Text type="h300">
                Submission summary by integration ({rangeLabel(range)})
            </Text>
            <Row spacing="300" widths="fill" alignmentVertical="stretch" wrap="down">
                {INTEGRATIONS.map((intg) => (
                    <IntegrationCard
                        key={intg}
                        intg={intg}
                        counts={countsFor(intg)}
                        onViewFailures={(bucket) => setDrawer({ integration: intg, bucket })}
                    />
                ))}
            </Row>
            {metricsError ? (
                <Text type="b100" color="error">
                    {metricsError}
                </Text>
            ) : null}

            {/* Pending ASNs worklist */}
            <PendingAsns />

            {/* Upload ASN file */}
            <Text type="h300">Upload ASN file</Text>
            <UploadAsn />
        </Column>
    );
}
